import { useCallback } from 'react';
import { toPairs, fromPairs } from 'ramda';
import { useMutation } from '@apollo/react-hooks';
import { tc } from '../../lib/trackingContext';
import { deferUntilNewTitle } from '../../lib/deferUntilNewTitle';
import { getCookies } from '../../lib/getCookies';
import { getGAClientId } from '../../lib/getGAClientId';
import { getGATrackingId } from '../../lib/getGATrackingId';
import { useCountry } from '../../components/CountrySelector';
import { useSegmentAnonymousId } from '../useSegmentAnonymousId';
import { useCart, useCartItems } from '../useCart';
import { useUser } from '../useUser';
import { useSignifydSessionId } from '../useSignifydSessionId';
import { GET_REWARDS_SUMMARY_QUERY } from '../useRewardsSummary';
import {
  PLACE_ORDER_MUTATION,
  PlaceOrderMutation,
  PlaceOrderMutationVariables
} from './placeOrderMutation.rest';

export const usePlaceOrderMutation = () => {
  const segmentAnonymousId = useSegmentAnonymousId();
  const { country } = useCountry();
  const { signifydSessionId, clearSignifydSessionId } = useSignifydSessionId();
  const {
    cart: {
      id: cartId,
      attributes: { addressId, giftCode, paymentMethodId, selectedShippingMethodId, paymentSource }
    }
  } = useCart();
  const { cartItems } = useCartItems();
  const {
    user: { isLoggedIn }
  } = useUser();
  const [mutate, { loading, error }] = useMutation<PlaceOrderMutation, PlaceOrderMutationVariables>(
    PLACE_ORDER_MUTATION,
    {
      refetchQueries: isLoggedIn
        ? [
            {
              query: GET_REWARDS_SUMMARY_QUERY,
              variables: {
                countryCode: country.alpha2Code,
                currency: country.currencyCode
              }
            }
          ]
        : []
    }
  );

  const handlePlaceOrder = useCallback(async () => {
    if (!addressId) return Promise.reject('placeOrderMutation: no address selected');
    if (!cartId) return Promise.reject('placeOrderMutation: cart id not found');
    if (!paymentMethodId) return Promise.reject('placeOrderMutation: no payment method selected');
    if (!selectedShippingMethodId)
      return Promise.reject('placeOrderMutation: no shipping method selected');
    if (!paymentSource) return Promise.reject('placeOrderMutation: payment source not found');

    const cookies = getCookies();

    const result = await mutate({
      variables: {
        input: {
          data: {
            type: 'orders',
            attributes: {
              address_id: addressId,
              cart_id: cartId,
              country_code: country.alpha2Code,
              event_source: 'astrarium',
              offer_code: giftCode,
              order_session_id: signifydSessionId,
              payment_id: paymentMethodId,
              payment_source: paymentSource,
              shipment_method: selectedShippingMethodId,
              linkshare: cookies.linkshare,
              ga_id: getGAClientId(),
              ga_tracking_id: getGATrackingId(),
              segment_id: segmentAnonymousId
            }
          }
        }
      }
    });

    clearSignifydSessionId();

    const sessionParameters = JSON.parse(
      window?.sessionStorage.getItem('sessionQueryParams') ?? '{}'
    );
    // This is to coerce the keys to lowercase, because query params are case insensitive.
    const referralParameters = fromPairs(
      toPairs(sessionParameters).map(entry => [entry[0].toLowerCase(), entry[1]])
    );

    deferUntilNewTitle(() => {
      tc.track('Order Converted', {
        addToScopeOnce: {
          order: result.data?.checkout.data[0],
          products: cartItems,
          referralParameters
        }
      });
    });

    return result;
  }, [
    cartId,
    cartItems,
    addressId,
    giftCode,
    paymentMethodId,
    selectedShippingMethodId,
    paymentSource,
    country,
    mutate,
    signifydSessionId,
    clearSignifydSessionId,
    segmentAnonymousId
  ]);

  return [handlePlaceOrder, { loading, error }] as const;
};
